import React from "react";
import { Linkedin, Youtube, Instagram } from "lucide-react";
import { Link } from "react-router-dom";

// X (formerly Twitter) glyph — inline SVG, matches lucide stroke style
function XIcon({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M18.244 2H21.5l-7.55 8.63L23 22h-6.94l-5.43-7.09L4.4 22H1.14l8.09-9.25L1 2h7.1l4.91 6.48L18.244 2Zm-1.218 18h1.87L7.06 4H5.12l11.906 16Z" />
    </svg>
  );
}

const SOCIALS = [
  {
    name: "X",
    Icon: XIcon,
    href: "https://x.com/Tradecure_in?t=zwprDVqHjN13pDtnKb_QgA&s=09",
  },
  {
    name: "LinkedIn",
    Icon: Linkedin,
    href: "https://www.linkedin.com/company/tradecure-in/posts/?feedView=all",
  },
  {
    name: "YouTube",
    Icon: Youtube,
    href: "https://youtube.com/@tradecure_in?si=EhgBMVePNFDxeZFv",
  },
  {
    name: "Instagram",
    Icon: Instagram,
    href: "https://www.instagram.com/tradecure.in?igsh=MXc4dHpmbHE3bnRnOA%3D%3D",
  },
];

const COLUMNS = [
  {
    h: "Product",
    items: [
      { label: "Features", to: "/#features", type: "anchor" },
      { label: "Strategies", to: "/#strategies", type: "anchor" },
      { label: "Pricing", to: "/#pricing", type: "anchor" },
      { label: "Roadmap", to: "#", type: "anchor" },
    ],
  },
  {
    h: "Learn",
    items: [
      { label: "Knowledge Hub", to: "/knowledge-hub", type: "route" },
      { label: "Blog", to: "/blog", type: "route" },
      { label: "FAQ", to: "/#faq", type: "anchor" },
      { label: "Join Waitlist", to: "/#waitlist", type: "anchor" },
    ],
  },
  {
    h: "Company",
    items: [
      { label: "About", to: "#", type: "anchor" },
      { label: "Careers", to: "#", type: "anchor" },
      { label: "Press", to: "#", type: "anchor" },
      { label: "Contact", to: "#", type: "anchor" },
    ],
  },
  {
    h: "Legal",
    items: [
      { label: "Privacy", to: "#", type: "anchor" },
      { label: "Terms", to: "#", type: "anchor" },
      { label: "Disclaimer", to: "#", type: "anchor" },
      { label: "SEBI info", to: "#", type: "anchor" },
    ],
  },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0f172a] text-white/80 pt-16 pb-10"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="flex items-center gap-2.5">
              <img
                src="/tradecure-logo.png"
                alt="Tradecure"
                className="h-9 w-9 object-contain"
                width="36"
                height="36"
              />
              <span className="font-heading text-2xl font-extrabold tracking-tight text-white">
                tradecure
              </span>
            </div>
            <p className="mt-5 max-w-sm text-white/65 leading-relaxed">
              The no-code, no-prompt automation platform built for the 90% of
              traders who deserve discipline without the jargon.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {SOCIALS.map((s, i) => (
                <a
                  key={i}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${s.name.toLowerCase()}`}
                  aria-label={s.name}
                  className="h-9 w-9 rounded-md border border-white/15 hover:border-white/40 hover:bg-white/5 text-white/80 hover:text-white flex items-center justify-center transition"
                >
                  <s.Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
            {COLUMNS.map((col, i) => (
              <div key={i}>
                <div className="font-heading font-bold text-white mb-4">
                  {col.h}
                </div>
                <ul className="space-y-2.5">
                  {col.items.map((it, j) =>
                    it.type === "route" ? (
                      <li key={j}>
                        <Link
                          to={it.to}
                          data-testid={`footer-link-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className="text-white/60 hover:text-white transition"
                        >
                          {it.label}
                        </Link>
                      </li>
                    ) : (
                      <li key={j}>
                        <a
                          href={it.to}
                          data-testid={`footer-link-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className="text-white/60 hover:text-white transition"
                        >
                          {it.label}
                        </a>
                      </li>
                    )
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-white/50 max-w-3xl leading-relaxed">
            © {new Date().getFullYear()} Tradecure. Trading in securities
            markets carries risk. Tradecure is a technology platform. Historical
            performance is not indicative of future returns. Always trade with
            capital you can afford to lose.
          </p>
          <div className="flex items-center gap-2 font-mono text-xs text-white/50">
            <span className="h-1.5 w-1.5 rounded-full bg-[#10b981] pulse-dot" />
            All systems operational
          </div>
        </div>
      </div>
    </footer>
  );
}
